'use strict';

/**
 * Tracking consent pop-in, inspired by the Gesec implementation
 * Services come from PHP (Sevanova_Tracking) through `window.GRAHOU_tracking`
 * Custom services dispatch `GRAHOU_tracking_consent_{slug}` / `GRAHOU_tracking_refuse_{slug}` on the body
 */
const PREFIX = 'GRAHOU_tracking_consent-';

const hasCookie = (name) => document.cookie.split('; ').some((cookie) => cookie.startsWith(PREFIX + name + '='));
const setCookie = (name, days) => {
    document.cookie = `${PREFIX}${name}=1; max-age=${days * 86400}; path=/; SameSite=Lax`;
};
const removeCookie = (name) => {
    document.cookie = `${PREFIX}${name}=; max-age=0; path=/; SameSite=Lax`;
};

export default new class Tracking {
    #$popIn;
    #$form;
    #$formToggle;
    #$servicesWrapper;
    #$$input;
    #services = {};
    #expirationDays = 180;

    /**
     * Is the first opening of the pop-in? Services are (re)applied on page load
     * @type {boolean}
     */
    #isFirstOpening = true;

    init() {
        this.#$popIn = document.getElementById('tracking_consent_pop_in');

        if (!this.#$popIn || !window.GRAHOU_tracking) {
            return;
        }

        this.#$form = this.#$popIn.querySelector('.tracking_consent_pop_in--form');
        this.#$formToggle = this.#$popIn.querySelector('.tracking_consent_pop_in--form-services_toggle');
        this.#$servicesWrapper = this.#$popIn.querySelector('.tracking_consent_pop_in--form-services_wrapper');
        this.#$$input = this.#$popIn.querySelectorAll('.tracking_consent_pop_in--form-service-input');
        this.#$servicesWrapper.inert = true;

        window.GRAHOU_tracking.services.forEach((service) => {
            this.#services[service.slug] = service;
        });
        this.#expirationDays = Number(window.GRAHOU_tracking.expirationDays);

        this.#$formToggle.addEventListener('click', this.#toggleServices.bind(this));
        this.#$popIn.querySelector('#tracking_consent_pop_in--btn__refuse').addEventListener('click', () => this.#saveCookies(false));
        this.#$popIn.querySelector('#tracking_consent_pop_in--btn__accept').addEventListener('click', () => this.#saveCookies(true));
        this.#$form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.#saveCookies(null);
        });

        this.#checkCookies();
    }

    /**
     * Show the pop-in if no choice has been made yet, otherwise add the accepted services
     */
    #checkCookies() {
        if (!hasCookie('submitted')) {
            this.#$popIn.showPopover();
            return;
        }

        Object.values(this.#services).forEach((service) => {
            if (this.getCookieAcceptance(service.slug)) {
                this.#addService(service);
            }
        });

        this.#isFirstOpening = false;
        this.#updateCheckboxes();
    }

    #toggleServices() {
        const open = this.#$form.classList.toggle('open');
        this.#$servicesWrapper.inert = !open;
        this.#$formToggle.setAttribute('aria-expanded', open);
    }

    /**
     * @param {boolean|null} accept true: accept all, false: refuse all, null: use the checkboxes
     */
    #saveCookies(accept) {
        if (this.#isFirstOpening) {
            this.#isFirstOpening = false;
        }
        else {
            document.querySelector('button[popovertarget="tracking_consent_pop_in"]')?.focus();
        }

        setCookie('submitted', this.#expirationDays);

        Object.values(this.#services).forEach((service) => {
            const checked = document.getElementById('tracking_consent-' + service.slug)?.checked;

            if (accept === true || (accept === null && checked)) {
                this.#addService(service);
            }
            else {
                this.#removeService(service);
            }
        });

        this.#updateCheckboxes();
        this.#$popIn.hidePopover();
    }

    #addService(service) {
        if (!this.#isFirstOpening && this.getCookieAcceptance(service.slug)) {
            return;
        }

        if (service.type === 'gtag') {
            this.#updateGTAG(service, 'granted');
        }
        else if (service.type === 'matomo') {
            window._paq?.push(['rememberCookieConsentGiven']);
        }
        else if (service.type === 'custom') {
            document.body.append(document.createRange().createContextualFragment(service.script));
        }

        setCookie(service.slug + '_accepted', this.#expirationDays);
    }

    #removeService(service) {
        if (!this.#isFirstOpening && !this.getCookieAcceptance(service.slug)) {
            return;
        }

        if (service.type === 'gtag') {
            this.#updateGTAG(service, 'denied');
        }
        else if (service.type === 'matomo') {
            window._paq?.push(['forgetCookieConsentGiven']);
        }
        else if (service.type === 'custom') {
            // ponytail: an already loaded third-party script keeps running until the next page load
            document.getElementById('GRAHOU_tracking_service_script-' + service.slug)?.remove();
            document.body.dispatchEvent(new CustomEvent('GRAHOU_tracking_refuse_' + service.slug));
        }

        removeCookie(service.slug + '_accepted');
    }

    #updateGTAG(service, status) {
        if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', Object.fromEntries(service.consent.map((param) => [param, status])));
        }
    }

    #updateCheckboxes() {
        this.#$$input.forEach(($el) => {
            $el.checked = this.getCookieAcceptance($el.dataset.service_slug);
        });
    }

    /**
     * @param {string} serviceSlug
     * @return {boolean}
     */
    getCookieAcceptance(serviceSlug) {
        return hasCookie(serviceSlug + '_accepted');
    }

    /**
     * Accept a single service from outside the pop-in (an embed "accept" button for example)
     * @param {string} serviceSlug
     */
    addTrackingService(serviceSlug) {
        const service = this.#services[serviceSlug];

        if (service) {
            this.#addService(service);
            document.getElementById('tracking_consent-' + serviceSlug).checked = true;
        }
    }
}();
